"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Flame, Zap } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function DevilCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">The Devil</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">The Devil</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-devil.svg"
                  alt="The Devil Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XV</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">The Devil</h1>
                  <p className="text-xl text-red-400">Şeytan / Bağımlılık</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Bağımlılık, kısıtlama ve materyalist tuzakların kartı. Kendi kendini
                aldattığınız alanları ve özgürlük yanılsamasını temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Bağımlılık</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Kısıtlama</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Materiyalizm</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Tuzaklar</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bağımlılıkların Farkında Olma</h3>
                  <p className="text-slate-400 text-sm">Kısıtlayıcı alışkanlıkları ve davranışları tanıma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gölge İle Yüzleşme</h3>
                  <p className="text-slate-400 text-sm">İnkar edilen yanları kabul etme ve entegre etme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Tutku ve Enerji</h3>
                  <p className="text-slate-400 text-sm">Ham yaşam enerjisi, güçlü cinsellik ve yaratıcılık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Özgürleşme İhtiyacı</h3>
                  <p className="text-slate-400 text-sm">Kısıtlamalardan kurtulma zamanı geldiğini anlama.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Flame className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bağımlılıktan Kurtulma</h3>
                  <p className="text-slate-400 text-sm">Olumsuz alışkanlıkları terk etme, kendini özgürleştirme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kendini Aldatma</h3>
                  <p className="text-slate-400 text-sm">Durumun gerçekliğini görmezden gelme, inkar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gölge Projeksiyonu</h3>
                  <p className="text-slate-400 text-sm">Kendi olumsuz yanlarını başkalarında görme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhsal Özgürleşme</h3>
                  <p className="text-slate-400 text-sm">Eski kalıplardan kurtuluş, yeniden doğuş.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👹</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Şeytan Figürü</h3>
              <p className="text-slate-400 text-sm">Gölge benlik, bastırılan içgüdüler ve korkuların temsili.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⛓️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Zincirler</h3>
              <p className="text-slate-400 text-sm">Kendi yarattığımız kısıtlamalar, gevşek bağlar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flame className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ters Beşgen</h3>
              <p className="text-slate-400 text-sm">Maddesel dünyanın egemenliği, ruhsal dengesizlik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🦇</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yarasa Kanatları</h3>
              <p className="text-slate-400 text-sm">Karanlık, bilinçsizlik ve gece enerjileri.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🔥</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Meşale</h3>
              <p className="text-slate-400 text-sm">Yanılsama ışığı, gerçek aydınlanmanın taklidi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👥</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Zincirleme İnsanlar</h3>
              <p className="text-slate-400 text-sm">Ortak bağımlılıklar, toksik ilişkiler.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Toksik ilişki dinamikleri</li>
                <li>• Cinsel obsesyonlar</li>
                <li>• Ortak bağımlılık</li>
                <li>• İlişkide kontrol ve manipülasyon</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İş yerinde manipülasyon</li>
                <li>• Para tutkusu</li>
                <li>• Etik dışı davranışlar</li>
                <li>• İş bağımlılığı</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Gölge çalışması yapma</li>
                <li>• Bağımlılıklarla yüzleşme</li>
                <li>• Kısıtlayıcı inançları sorgulama</li>
                <li>• İçsel özgürleşme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-400" />
                Sağlık ve Alışkanlıklar
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Zararlı alışkanlıklar</li>
                <li>• Madde bağımlılığı</li>
                <li>• Obsesif davranışlar</li>
                <li>• Terapi ve rehabilitasyon</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/temperance"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Temperance
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/tower"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            The Tower
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}